import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { track, Events } from '../analytics';

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    backdropFilter: 'blur(4px)',
    padding: '16px',
  },
  modal: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    maxWidth: '340px',
    width: '100%',
    textAlign: 'center',
    position: 'relative',
    '@media (min-width: 768px)': {
      padding: '40px',
      maxWidth: '400px',
    },
  },
  closeButton: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#70757a',
    padding: '4px',
    borderRadius: '4px',
    transition: 'background-color 0.2s ease',
    '@media (min-width: 768px)': {
      top: '16px',
      right: '16px',
      fontSize: '24px',
    },
  },
  icon: {
    fontSize: '48px',
    marginBottom: '20px',
    '@media (min-width: 768px)': {
      fontSize: '64px',
      marginBottom: '24px',
    },
  },
  title: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '12px',
    '@media (min-width: 768px)': {
      fontSize: '24px',
      marginBottom: '16px',
    },
  },
  subtitle: {
    fontSize: '14px',
    color: '#5f6368',
    marginBottom: '24px',
    lineHeight: '1.5',
    '@media (min-width: 768px)': {
      fontSize: '16px',
      marginBottom: '32px',
    },
  },
  errorMessage: {
    color: '#d93025',
    fontSize: '12px',
    marginTop: '12px',
    padding: '8px',
    backgroundColor: '#fce8e6',
    borderRadius: '4px',
    '@media (min-width: 768px)': {
      fontSize: '14px',
      marginTop: '16px',
    },
  },
  loadingSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #ffffff',
    borderTop: '2px solid transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    '@media (min-width: 768px)': {
      width: '20px',
      height: '20px',
    },
  },
};

// Helper function to apply responsive styles
const applyResponsiveStyles = (baseStyles) => {
  const mediaQueries = baseStyles['@media (min-width: 768px)'] || {};

  // Create a copy without media queries for base styles
  const { '@media (min-width: 768px)': _, ...cleanStyles } = baseStyles;

  // Apply media queries based on window width
  if (typeof window !== 'undefined' && window.innerWidth >= 768) {
    Object.assign(cleanStyles, mediaQueries);
  }

  return cleanStyles;
};

// Add keyframes for loading spinner and Google button styles
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .gsi-material-button {
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    -webkit-appearance: none;
    background-color: #ffffff;
    background-image: none;
    border: 1px solid #dadce0;
    border-radius: 20px;
    box-sizing: border-box;
    color: #3c4043;
    cursor: pointer;
    font-family: 'Roboto', arial, sans-serif;
    font-size: 13px;
    height: 36px;
    letter-spacing: 0.25px;
    outline: none;
    overflow: hidden;
    padding: 0 10px;
    position: relative;
    text-align: center;
    transition: background-color .218s, border-color .218s, box-shadow .218s;
    vertical-align: middle;
    white-space: nowrap;
    width: 100%;
    max-width: 100%;
    min-width: min-content;
  }

  @media (min-width: 768px) {
    .gsi-material-button {
      border-radius: 24px;
      font-size: 14px;
      height: 40px;
      padding: 0 12px;
      max-width: 400px;
    }
  }

  .gsi-material-button:hover {
    box-shadow: 0 1px 2px 0 rgba(60, 64, 67, .30), 0 1px 3px 1px rgba(60, 64, 67, .15);
  }

  .gsi-material-button:active {
    box-shadow: 0 1px 2px 0 rgba(60, 64, 67, .30), 0 2px 6px 2px rgba(60, 64, 67, .15);
  }

  .gsi-material-button:disabled {
    cursor: not-allowed;
    opacity: .38;
  }

  .gsi-material-button-state {
    -webkit-transition: opacity .218s;
    transition: opacity .218s;
    bottom: 0;
    left: 0;
    opacity: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  .gsi-material-button:hover .gsi-material-button-state {
    background-color: #303030;
    opacity: .08;
  }

  .gsi-material-button:focus .gsi-material-button-state {
    background-color: #303030;
    opacity: .12;
  }

  .gsi-material-button:active .gsi-material-button-state {
    background-color: #303030;
    opacity: .12;
  }

  .gsi-material-button-content-wrapper {
    -webkit-align-items: center;
    align-items: center;
    display: flex;
    -webkit-flex-direction: row;
    flex-direction: row;
    -webkit-flex-wrap: nowrap;
    flex-wrap: nowrap;
    height: 100%;
    justify-content: space-between;
    position: relative;
    width: 100%;
  }

  .gsi-material-button-icon {
    height: 16px;
    margin-right: 8px;
    min-width: 16px;
    width: 16px;
  }

  @media (min-width: 768px) {
    .gsi-material-button-icon {
      height: 20px;
      margin-right: 12px;
      min-width: 20px;
      width: 20px;
    }
  }

  .gsi-material-button-contents {
    -webkit-flex-grow: 1;
    flex-grow: 1;
    font-family: 'Roboto', arial, sans-serif;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: top;
  }

  .gsi-material-button svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`;
document.head.appendChild(styleSheet);

export const LoginModal = ({ isOpen, onClose, onSuccess, context = 'default', strings = {} }) => {
  const { loginWithGoogle, error } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const isCheckout = context === 'checkout';

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const user = await loginWithGoogle();
      track(Events.LOGIN_COMPLETED, { method: 'google' });
      if (onSuccess) await onSuccess(user);
      onClose();
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={applyResponsiveStyles(styles.modal)}>
        <button
          style={applyResponsiveStyles(styles.closeButton)}
          onClick={onClose}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f3f4'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          ×
        </button>

        <div style={applyResponsiveStyles(styles.icon)}>🔐</div>

        <h2 style={applyResponsiveStyles(styles.title)}>
          {isCheckout ? (strings.checkoutLoginTitle || 'Sign in to continue') : 'Login Required'}
        </h2>

        <p style={applyResponsiveStyles(styles.subtitle)}>
          {isCheckout
            ? (strings.checkoutLoginLead || 'Connect your Google account so we can create your secure payment invoice.')
            : 'To prevent abuse, we require you to sign in with your Google account.'}
        </p>

        <button
          className="gsi-material-button"
          onClick={handleGoogleLogin}
          disabled={isLoading}
        >
          <div className="gsi-material-button-state"></div>
          <div className="gsi-material-button-content-wrapper">
            <div className="gsi-material-button-icon">
              {isLoading ? (
                <div style={applyResponsiveStyles(styles.loadingSpinner)}></div>
              ) : (
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlnsXlink="http://www.w3.org/1999/xlink" style={{display: 'block'}}>
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                  <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
              )}
            </div>
            <span className="gsi-material-button-contents">Continue with Google</span>
            <span style={{display: 'none'}}>Continue with Google</span>
          </div>
        </button>

        {error && (
          <div style={applyResponsiveStyles(styles.errorMessage)}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
